import { makeObservable, observable, action } from 'mobx'
import WorkspaceOpportunityInvitationStore from './WorkspaceOpportunityInvitationStore'
import { AlertFactory, AlertState, Alert } from '../../alerts/Alert'
import { tapAction } from '../../utils/mobx-util'
import { WorkspaceInvitationModalViewModel } from './WorkspaceInvitationModalViewModel'
import WorkspaceInvitation from './WorkspaceInvitation'
import { orderBy } from 'lodash'
import { formattedTimeUntil } from '../../utils/date-util'

/**
 * View Model for managing the Modal View Modals and Alerts for Workspace Invitations
 * from Workspace Opportunities
 * ToDo: Add RealTime Events once it is supported
 */
export default class WorkspaceOpportunityInvitationsViewModel {
  /**
   * View Models for modals that are actively being shown.
   */
  invitationModalViewModel: WorkspaceInvitationModalViewModel[] = []

  /**
   * View Models for modals that are buffered behind a toast and not yet shown.
   */
  backgroundViewModels: WorkspaceInvitationModalViewModel[] = []

  /**
   * A toast that indicates pending invitations to Workspaces.  Associated with the
   * toast is a "batch" of buffered invitations behind it.
   */
  _offerToast: Alert | null = null

  constructor(
    private readonly _workspaceOpportunityInvitationStore: WorkspaceOpportunityInvitationStore,
    private readonly _newAlert: AlertFactory
  ) {
    makeObservable(this, {
      invitationModalViewModel: observable,
      backgroundViewModels: observable,
      showBufferedOffers: action.bound,
    })

    // Update teh VM collection for each incoming invitation
    _workspaceOpportunityInvitationStore.incomingInvitations$
      .pipe(
        tapAction((invitation) => {
          this.backgroundViewModels.push(this.createVm(invitation))

          if (!this._offerToast) {
            this._offerToast = this.setupOfferToast()
          }

          this._offerToast.show()
        })
      )
      .subscribe()
  }

  /**
   * Shows the buffered invitations and dismisses the toast.
   */
  showBufferedOffers() {
    this.invitationModalViewModel = this.invitationModalViewModel.concat(
      sortedByPriority(this.backgroundViewModels)
    )

    this.backgroundViewModels = []

    this.invitationModalViewModel.forEach((vm) => vm.show())
    this._offerToast?.dismiss()
    this._offerToast = null
  }

  /**
   * Configures teh invitation toast with its interaction handler
   * and reactive title and description
   */
  private setupOfferToast() {
    return this._newAlert()
      .withState(AlertState.Success)
      .saying(this.toastTitle, this.toastDescription)
      .onInteract(this.showBufferedOffers)
      .withActions({
        title: 'Review',
        handler: this.showBufferedOffers,
      })
      .cancelAutoDismiss()
  }

  /**
   * Reactive getter for the toast title
   */
  private toastTitle = () => {
    return this.backgroundViewModels.length > 1
      ? `You're invited to ${this.backgroundViewModels.length} workspaces!`
      : `You're invited to a workspace!`
  }

  /**
   * Reactive getter for the toast description
   */
  private toastDescription = () => {
    if (this.backgroundViewModels.length === 0) {
      return ''
    }

    if (this.backgroundViewModels.length > 1) {
      const highestPriorityVm = orderBy(
        this.backgroundViewModels,
        (vm) => vm.expirationMillis
      )[0]

      return `Auto-rejecting in ${formattedTimeUntil(
        highestPriorityVm.timeUntilExpiration!
      )}`
    }

    return `Auto-rejecting in ${formattedTimeUntil(
      this.backgroundViewModels[0].timeUntilExpiration!
    )}`
  }

  private createVm(invitation: WorkspaceInvitation) {
    return new WorkspaceInvitationModalViewModel(
      this._workspaceOpportunityInvitationStore,
      invitation
    )
  }
}

/**
 * Sorts a list of view models by their expiration millis in descending order.
 * Descending order is used so that the offer that will expire soonest is at the
 * end, thus allowing its modal to be drawn over all the others.
 */
function sortedByPriority(vms: WorkspaceInvitationModalViewModel[]) {
  return orderBy(vms, (vm) => vm.expirationMillis, 'desc')
}
